import React, { useState, useEffect } from 'react';
import { 
    CardNumberElement, 
    CardExpiryElement, 
    CardCvcElement, 
    useStripe, 
    useElements 
} from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import stripeLogo from '../../assets/stripe-logo.png';
import { useProfile } from '../../context/ProfileContext'; // ✅ Added context

const SaveCardForm = ({ onSaveSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { isDarkMode } = useProfile(); // ✅ Grab theme state
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // ✅ Dynamic Stripe Styles
    const CARD_ELEMENT_OPTIONS = {
        style: {
            base: {
                color: isDarkMode ? "#FFFFFF" : "#32325d", // ✅ White text for dark mode
                fontFamily: 'Arial, sans-serif',
                fontSmoothing: "antialiased",
                fontSize: "16px",
                "::placeholder": {
                    color: isDarkMode ? "#737373" : "#aab7c4",
                },
            },
            invalid: {
                color: "#FF00CC", // ✅ Match brand-pink for errors
                iconColor: "#FF00CC",
            },
        },
    };

    useEffect(() => {
        const functions = getFunctions();
        const createSetupIntent = httpsCallable(functions, 'createSetupIntent');
        
        createSetupIntent()
            .then(result => setClientSecret(result.data.clientSecret))
            .catch(err => setError("Could not connect to payment server."));
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements || !clientSecret) return;
        setIsLoading(true);

        const cardElement = elements.getElement(CardNumberElement);

        const { error: setupError } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: { card: cardElement },
        });

        if (setupError) {
            setError(setupError.message);
            setIsLoading(false);
        } else {
            setError(null);
            setIsLoading(false);
            onSaveSuccess();
        }
    };

    return (
        <form onSubmit={handleSubmit} className="transition-colors">
            <h4 className="text-lg font-semibold mb-2 dark:text-brand-white">Save Payment Method</h4>
            <p className="text-sm text-gray-500 dark:text-brand-gray mb-4">Your card will be securely saved for future one-touch payments.</p>
            
            {/* ✅ Outer Wrapper Box */}
            <div className="p-3 border dark:border-brand-gray rounded-md space-y-4 bg-gray-100 dark:bg-brand-gray/10 transition-colors">
                
                {/* Card Number */}
                <div className="w-full">
                    <label className="text-xs font-semibold text-gray-600 dark:text-brand-gray mb-1 block">Card Number</label>
                    <div className="p-2.5 border border-slate-300 dark:border-brand-gray rounded bg-white dark:bg-brand-black shadow-sm transition-colors">
                        <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                </div>

                {/* Expiry and CVC */}
                <div className="flex space-x-4">
                    <div className="w-1/2">
                        <label className="text-xs font-semibold text-gray-600 dark:text-brand-gray mb-1 block">Expiration</label>
                        <div className="p-2.5 border border-slate-300 dark:border-brand-gray rounded bg-white dark:bg-brand-black shadow-sm transition-colors">
                            <CardExpiryElement options={CARD_ELEMENT_OPTIONS} />
                        </div>
                    </div>
                    <div className="w-1/2">
                        <label className="text-xs font-semibold text-gray-600 dark:text-brand-gray mb-1 block">CVC</label>
                        <div className="p-2.5 border border-slate-300 dark:border-brand-gray rounded bg-white dark:bg-brand-black shadow-sm transition-colors">
                            <CardCvcElement options={CARD_ELEMENT_OPTIONS} />
                        </div>
                    </div>
                </div>
            </div>

            {error && <div className="text-brand-pink text-sm mt-2 font-bold">{error}</div>}
            
            {/* ✅ Brand Consistent Button */}
            <button 
                type="submit" 
                disabled={!stripe || isLoading}
                className="w-full bg-brand-pink hover:brightness-110 active:brightness-90 text-white font-bold py-3 px-4 rounded-xl mt-6 transition-all disabled:opacity-50 disabled:bg-brand-gray"
            >
                {isLoading ? "Saving..." : "Save Card"}
            </button>

            <div className="flex items-center justify-center mt-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 dark:text-brand-gray mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 2a3 3 0 00-3 3v1H6a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2V8a2 2 0 00-2-2h-1V5a3 3 0 00-3-3zm-1 4a1 1 0 10-2 0v1h2V6z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-gray-500 dark:text-brand-gray font-semibold mr-2">
                    Secured by Stripe
                </span>
                <img src={stripeLogo} alt="Stripe" className="h-4 w-auto dark:opacity-50" />
            </div>
        </form>
    );
};

export default SaveCardForm;